import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Upload, CheckCircle2, Clock, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import ProviderLoadingScreen from '../components/ProviderLoadingScreen';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderKYC() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [docType, setDocType] = useState('cedula');
  const [docNumber, setDocNumber] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/provider/verification`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setStatus(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) { setError(t('providerKyc.uploadRequired', 'Sube una foto clara de tu documento.')); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('document_number', docNumber);
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/provider/verification`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('providerKyc.submitError', 'No se pudo enviar la verificación.'));
      setStatus({ verification_status: 'pending', document_type: docType, document_number: docNumber, has_document: true, verified: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const vstatus = status?.verified ? 'approved' : (status?.verification_status || 'none');

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 max-w-2xl py-8">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#36C0C7] to-[#134168] flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-[#111827]">{t('providerKyc.title', 'Verificación de identidad')}</h1>
              <p className="text-sm text-gray-500">{t('providerKyc.subtitle', 'Requisito para publicar servicios como proveedor')}</p>
            </div>
          </div>

          {loading ? (
            <ProviderLoadingScreen chrome={false} className="py-10" message={t('providerKyc.loading', 'Cargando verificación…')} />
          ) : vstatus === 'approved' ? (
            <div className="mt-6 flex flex-col items-center text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-900">{t('providerKyc.approvedTitle', '¡Identidad verificada!')}</h2>
              <p className="text-gray-500 mt-1 mb-6">{t('providerKyc.approvedBody', 'Ya puedes publicar tus servicios.')}</p>
              <button onClick={() => navigate('/provider/assets')} className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white font-bold">
                {t('providerKyc.postServiceBtn', 'Publicar un servicio')}
              </button>
            </div>
          ) : vstatus === 'pending' ? (
            <div className="mt-6 flex flex-col items-center text-center py-8">
              <Clock className="w-16 h-16 text-amber-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-900">{t('providerKyc.pendingTitle', 'Documento en revisión')}</h2>
              <p className="text-gray-500 mt-1 max-w-md">
                {t('providerKyc.pendingBody', 'Recibimos tu {{docType}}. Nuestro equipo lo revisará pronto. Te avisaremos cuando tu cuenta esté verificada.', {
                  docType: status?.document_type === 'pasaporte' ? t('providerKyc.docTypePassport', 'pasaporte') : t('providerKyc.docTypeCedula', 'cédula'),
                })}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-5">
              {vstatus === 'rejected' && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                  <XCircle className="w-5 h-5 shrink-0" />
                  <span>{t('providerKyc.rejectedNotice', 'Tu verificación anterior fue rechazada. Vuelve a enviar un documento legible y vigente.')}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">{t('providerKyc.docTypeLabel', 'Tipo de documento')}</label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]">
                  <option value="cedula">{t('providerKyc.docTypeCedulaOption', 'Cédula de identidad')}</option>
                  <option value="pasaporte">{t('providerKyc.docTypePassportOption', 'Pasaporte')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('providerKyc.docNumberLabel', 'Número de documento')}</label>
                <input type="text" required value={docNumber} onChange={(e) => setDocNumber(e.target.value)}
                  placeholder={docType === 'pasaporte' ? t('providerKyc.docNumberPlaceholderPassport', 'Ej. A1234567') : t('providerKyc.docNumberPlaceholderCedula', 'Ej. 001-1234567-8')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('providerKyc.docPhotoLabel', 'Foto del documento')}</label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-[#36C0C7] transition-colors">
                  {preview ? (
                    <img src={preview} alt={t('providerKyc.docPhotoAlt', 'documento')} className="max-h-48 rounded-lg object-contain" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">{t('providerKyc.docPhotoHint', 'Toca para subir (JPG/PNG, mín. 600×400)')}</span>
                    </>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
                </label>
                <p className="text-xs text-gray-400 mt-2">{t('providerKyc.docPhotoPrivacy', 'Asegúrate de que se lean nombre, foto y número. La imagen es privada y solo la ve nuestro equipo.')}</p>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white py-4 rounded-xl font-bold text-lg disabled:opacity-50">
                {submitting ? t('providerKyc.submitting', 'Enviando…') : t('providerKyc.submitBtn', 'Enviar para verificación')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
